import { createContext, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { getNotifSoundPrefs } from "@/lib/prefs";
import type { MotifEvent } from "@/lib/sijSound";

type ToastTone = "success" | "error" | "warning" | "info";

interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  message?: string;
}

interface ShowOptions {
  /** Set to false to silence the notification motif for this toast. */
  sound?: false;
}

interface ToastContextValue {
  show: (tone: ToastTone, title: string, message?: string, options?: ShowOptions) => void;
}

// Map toast tones → SijSound motifs. Tones without a dedicated motif fall back
// to "message" (the gentle ping).
const toneMotif: Record<ToastTone, MotifEvent> = {
  success: "success",
  error: "error",
  warning: "warning",
  info: "info",
};

/**
 * Play the notification motif for a toast, gated by the `notifSound` pref. The
 * SijSound engine is imported dynamically so it stays out of the initial bundle
 * and only loads the first time a toast actually fires.
 */
function playToastMotif(tone: ToastTone): void {
  if (!getNotifSoundPrefs().enabled) return;
  void import("@/lib/sijSound")
    .then(({ playMotif }) => playMotif(toneMotif[tone]))
    .catch(() => {
      /* audio is best-effort — never surface a failure */
    });
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { t: translate } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback(
    (tone: ToastTone, title: string, message?: string, options?: ShowOptions) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, tone, title, message }]);
      if (options?.sound !== false) playToastMotif(tone);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 3800);
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div
        role="region"
        aria-live="polite"
        aria-label={translate("nav.notifications")}
        className="pointer-events-none fixed bottom-5 start-5 z-[1000] flex max-w-sm flex-col gap-2.5"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.tone === "error" ? "alert" : "status"}
            className="pointer-events-auto flex items-start gap-3 rounded-lg border border-line/15 bg-card/85 p-3.5 shadow-lift backdrop-blur-md animate-[slidein_.25s_ease-out]"
          >
            <div className={cn("grid h-9 w-9 flex-shrink-0 place-items-center rounded-full shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]", toneBg[toast.tone])}>
              {icons[toast.tone]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-ink-1">{toast.title}</div>
              {toast.message && <div className="mt-0.5 text-xs text-ink-2">{toast.message}</div>}
            </div>
            <button
              type="button"
              onClick={() => setToasts((prev) => prev.filter((x) => x.id !== toast.id))}
              className="p-0.5 text-base leading-none text-ink-3 hover:text-ink-1"
              aria-label={translate("common.close")}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const toneBg: Record<ToastTone, string> = {
  success: "bg-green/10 text-green",
  error: "bg-rose/10 text-rose",
  warning: "bg-amber/10 text-amber",
  info: "bg-primary/10 text-primary",
};

const icons: Record<ToastTone, React.ReactNode> = {
  success: <Tick />,
  error: <Cross />,
  warning: <Bang />,
  info: <Info />,
};

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

function Tick() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7"/></svg>; }
function Cross() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>; }
function Bang() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 8v5M12 17h.01"/><circle cx="12" cy="12" r="9"/></svg>; }
function Info() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 7h.01"/></svg>; }
